import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { SvgIconComponent } from '@mui/icons-material';
import { CONTENT_SHAPE } from '../../constants/contentColors';
import { CONTENT_FIGURE, CONTENT_FIGURE_BASE } from '../../constants/contentTypography';
import { contentBorders } from '../../theme/createContentTheme';
import AnimatedFigure from '../metrics/AnimatedFigure';

export interface FlowStage {
  key: string;
  icon: SvgIconComponent;
  label: string;
  /** Pre-formatted figure. */
  value: string;
  unit?: string;
  caption?: React.ReactNode;
  /** Visually lead stage (the outcome). */
  emphasis?: boolean;
}

/**
 * Stages of material movement as a row of cards (request → in transit →
 * collected …). Only stages backed by real data are passed in — no invented
 * destinations.
 */
const CollectionFlow: React.FC<{ stages: FlowStage[]; ariaLabel: string }> = ({ stages, ariaLabel }) => {
  const theme = useTheme();
  const borders = contentBorders(theme);
  const columns = Math.min(Math.max(stages.length, 1), 5);
  return (
    <Box
      component="ul"
      aria-label={ariaLabel}
      sx={{
        listStyle: 'none',
        m: 0,
        p: 0,
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0, 1fr))',
          sm: `repeat(${Math.min(columns, 3)}, minmax(0, 1fr))`,
          lg: `repeat(${columns}, minmax(0, 1fr))`,
        },
      }}
    >
      {stages.map((stage) => {
        const Icon = stage.icon;
        return (
          <Box
            component="li"
            key={stage.key}
            sx={{
              minWidth: 0,
              p: { xs: 2, md: 2.5 },
              bgcolor: 'background.paper',
              border: `1px solid ${stage.emphasis ? alpha(theme.palette.primary.main, 0.4) : borders.default}`,
              borderRadius: `${CONTENT_SHAPE.md}px`,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.25,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
              <Box
                aria-hidden
                sx={{
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: `${CONTENT_SHAPE.sm}px`,
                  bgcolor: stage.emphasis
                    ? 'primary.main'
                    : alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.14 : 0.07),
                  color: stage.emphasis ? 'primary.contrastText' : 'primary.main',
                }}
              >
                <Icon sx={{ fontSize: 18 }} />
              </Box>
              <Typography variant="body2" component="p" sx={{ m: 0, color: 'text.secondary', fontWeight: 500 }} noWrap>
                {stage.label}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap' }}>
              <Typography component="p" sx={{ ...CONTENT_FIGURE_BASE, ...CONTENT_FIGURE.md, m: 0 }}>
                <bdi>
                  <AnimatedFigure value={stage.value} />
                </bdi>
              </Typography>
              {stage.unit ? (
                <Typography component="span" variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  {stage.unit}
                </Typography>
              ) : null}
            </Box>
            {stage.caption ? (
              <Typography variant="caption" component="p" sx={{ m: 0, color: 'text.secondary' }}>
                {stage.caption}
              </Typography>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
};

export default CollectionFlow;
